/**
 * Hero slideshow progress indicator.
 *
 * ONE continuous hairline for the WHOLE rotation - not one bar per slide, and
 * not a bar that resets on every slide. With 3 slides at 6s each the fill
 * travels 0 -> 100% across the full 18s and only returns to 0 when the deck
 * wraps back to the first slide:
 *
 *   progress = (currentSlideIndex * slideDuration + elapsedInCurrentSlide)
 *              / (slideCount * slideDuration)
 *
 * The fill itself is a CSS animation declared in snippets/slideshow-controls.liquid.
 * This element only does the things the stylesheet cannot do on its own:
 *
 *   1. Reads the real autoplay interval off <slideshow-component autoplay="N">,
 *      where N is the interval in seconds, and republishes the cycle timing as
 *      two custom properties on itself (they inherit down to the fill):
 *
 *        --slideshow-progress-duration = N * slideCount   (the whole cycle)
 *        --slideshow-progress-delay    = -(index * N)     (negative offset)
 *
 *      One animation, slideCount intervals long, started with a negative delay
 *      so it is already index/slideCount of the way through - precisely the
 *      current slide's starting proportion - and then carries on to 100% at the
 *      end of the last slide. Nothing about the timing is hard-coded: the
 *      MutationObserver below watches [autoplay], so changing the merchant's
 *      speed setting in the theme editor re-derives the maths live. slideCount
 *      comes from data-slide-count, written by the snippet from the section's
 *      own item_count.
 *
 *   2. Restarts the animation whenever the core component arms a NEW full
 *      interval, so the fill is re-based to the current slide's proportion
 *      rather than running on against a timer that just went back to zero.
 *      Those points are: every slideshow:select (autoplay tick, arrow, dot,
 *      swipe, and the infinite wraparound), every return from a hidden tab, and
 *      every time [paused] is cleared.
 *
 *      Slideshow#select() does NOT touch the running setInterval, so a
 *      user-initiated jump would leave the fill and the timer out of phase:
 *      click a dot or an arrow two seconds into a six second cycle and the fill
 *      would restart at that slide's proportion while the slide still flipped
 *      four seconds later. The timer is therefore re-armed through the
 *      component's own public resume() on user-initiated selects, which is also
 *      the behaviour a visitor expects - taking manual control restarts the
 *      dwell, it does not shorten it.
 *
 *   3. HOLDS THE DWELL ACROSS A HOVER INSTEAD OF RESTARTING IT. This is the one
 *      place the element deliberately overrides the core component. Slideshow
 *      binds mouseenter -> suspend() and mouseleave -> resume(), and resume() is
 *      pause() + play(), where play() arms a brand new setInterval for the FULL
 *      interval. So without intervention, hovering a hero for one second and
 *      leaving costs the visitor the entire dwell again, and the bar has to snap
 *      back to this slide's starting proportion to stay honest about it.
 *
 *      Instead the element keeps its own stopwatch for the current slide -
 *      accumulated while running, held while the fill is held - and on mouseleave
 *      it throws the component's fresh interval away with suspend() and replaces
 *      it with a one-shot timer for the time this slide actually had left. The
 *      bar is NOT restarted, so the CSS animation simply un-pauses and carries on
 *      from exactly where the pointer froze it; the one-shot fires at the moment
 *      the fill reaches the slide boundary, advances the deck, and hands the
 *      timing back to the component's own interval for the next slide.
 *
 *      suspend() is used rather than pause() on purpose: pause() would set
 *      [paused], which the stylesheet reads as "hold the fill", and the bar would
 *      freeze instead of continuing. suspend() clears the timer and nothing else.
 *
 *   4. Flags a hidden tab, because document.hidden has no CSS equivalent.
 *      A hidden tab still restarts the dwell rather than resuming it: the
 *      component suspends on hide and resumes with a full interval on show, and
 *      unlike a hover there is nobody watching the bar in the meantime.
 *
 *   5. Publishes --slideshow-progress-position so that under
 *      prefers-reduced-motion the bar can show how far through the deck the
 *      current slide is, statically, instead of animating.
 *
 * No core theme file is modified.
 */

const DURATION_PROPERTY = '--slideshow-progress-duration';
const DELAY_PROPERTY = '--slideshow-progress-delay';
const POSITION_PROPERTY = '--slideshow-progress-position';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class DetailerSlideshowProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #slideshow = null;

  /** @type {HTMLElement | null} */
  #fill = null;

  /** @type {Element[]} */
  #dots = [];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {MediaQueryList | null} */
  #motion = null;

  #index = 0;

  /**
   * Stopwatch for the CURRENT slide's dwell, in milliseconds. `#elapsed` is the
   * time already banked; `#runningSince` is the timestamp the clock last
   * started, or 0 while it is held. Wall time alone will not do - the fill is
   * held during a hover, so counting straight through would overstate how far
   * the slide had got and cut the remainder short.
   */
  #elapsed = 0;
  #runningSince = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #bridge;

  /**
   * True from the moment the pointer leaves until every microtask queued by
   * that same event has drained. See #handleSlideshowAttributes for why.
   * @type {boolean}
   */
  #leavingHover = false;

  connectedCallback() {
    const fill = this.querySelector('[data-progress-fill]');
    const slideshow = this.closest('slideshow-component');

    if (!(fill instanceof HTMLElement) || !(slideshow instanceof HTMLElement)) return;

    this.#fill = fill;
    this.#slideshow = slideshow;
    this.#dots = Array.from(this.closest('slideshow-controls')?.querySelectorAll('.slideshow-controls__dots button') ?? []);
    this.#motion = window.matchMedia(REDUCED_MOTION_QUERY);

    const { signal } = (this.#controller = new AbortController());

    slideshow.addEventListener('slideshow:select', this.#handleSelect, { signal });
    slideshow.addEventListener('mouseenter', this.#handleMouseEnter, { signal });
    slideshow.addEventListener('mouseleave', this.#handleMouseLeave, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    this.#motion.addEventListener('change', this.#handleMotionPreference, { signal });

    // [autoplay] can be toggled by the theme editor; [paused] is toggled by the
    // play/pause control and by the drag handler.
    this.#observer = new MutationObserver(this.#handleSlideshowAttributes);
    this.#observer.observe(slideshow, {
      attributes: true,
      attributeFilter: ['autoplay', 'paused'],
      attributeOldValue: true,
    });

    this.#setIndex(Number.parseInt(slideshow.getAttribute('initial-slide') ?? '0', 10) || 0);
    this.#handleVisibilityChange();
    this.#syncTiming();
    this.#restart();
    this.#beginSegment();
  }

  disconnectedCallback() {
    this.#cancelBridge();
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * The live autoplay interval in seconds - i.e. how long one slide is held -
   * read straight off <slideshow-component autoplay="N">, or 0 when the
   * slideshow does not rotate on a timer. Mirrors Slideshow#autoplayInterval,
   * so whatever the merchant sets in the theme editor is what the bar measures.
   * @type {number}
   */
  get #interval() {
    const seconds = Number.parseFloat(this.#slideshow?.getAttribute('autoplay') ?? '');

    return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  }

  /** @type {number} */
  get #slideCount() {
    const count = Number.parseInt(this.dataset.slideCount ?? '', 10);

    return Number.isFinite(count) && count > 0 ? count : 1;
  }

  /**
   * The slideshow's public playback API. The element is held as a plain
   * HTMLElement because it may not have upgraded yet, so every method is
   * treated as optional and every call site guards.
   * @type {{ suspend?: () => void, play?: () => void, next?: () => void, resume?: () => void }}
   */
  get #playback() {
    return /** @type {{ suspend?: () => void, play?: () => void, next?: () => void, resume?: () => void }} */ (
      /** @type {unknown} */ (this.#slideshow ?? {})
    );
  }

  /**
   * Publishes the timing of the single whole-cycle animation: its length, and
   * the negative offset that places its start at the current slide.
   *
   *   duration = interval * slideCount
   *   delay    = -(index * interval)
   *
   * so the fill reads (index * interval + elapsed) / (slideCount * interval) at
   * every moment, unbroken across slide changes.
   */
  #syncTiming() {
    const interval = this.#interval;

    if (!interval) {
      this.style.removeProperty(DURATION_PROPERTY);
      this.style.removeProperty(DELAY_PROPERTY);
      return;
    }

    this.style.setProperty(DURATION_PROPERTY, `${interval * this.#slideCount}s`);
    this.style.setProperty(DELAY_PROPERTY, `${-(this.#index * interval)}s`);
  }

  /**
   * Replays the CSS animation from its (negatively delayed) start. Dropping the
   * animation and forcing a reflow before handing it back to the stylesheet is
   * the cheapest reliable restart, and it lets animation-play-state be
   * re-derived from :hover / [paused] / [tab-hidden] rather than being pinned by
   * script. Because the delay is re-read on every replay, a restart resumes the
   * cycle at the current slide's proportion instead of snapping back to zero.
   *
   * Called only where the component has genuinely armed a fresh full interval.
   * Notably NOT on mouseleave - see #handleMouseLeave.
   */
  #restart() {
    const fill = this.#fill;

    if (!fill || !this.#interval) return;
    // Under reduced motion the fill is a static proportion, so there is nothing
    // to replay and no reason to pay for a reflow every interval.
    if (this.#motion?.matches) return;

    fill.style.animation = 'none';
    void fill.offsetWidth;
    fill.style.removeProperty('animation');
  }

  /* ---- Dwell stopwatch ---------------------------------------------------
   * Mirrors the fill: it runs while the fill runs and is held while the fill is
   * held, so #elapsed is always how much of THIS slide the visitor has actually
   * watched. The remainder is what the bridge timer in #handleMouseLeave hands
   * back to the slideshow.
   * ---------------------------------------------------------------------- */

  /** Starts this slide's dwell from zero. Use where a full interval was armed. */
  #beginSegment() {
    this.#elapsed = 0;
    this.#runningSince = performance.now();
  }

  /** Banks the time run so far and stops counting. */
  #holdSegment() {
    if (!this.#runningSince) return;

    this.#elapsed += performance.now() - this.#runningSince;
    this.#runningSince = 0;
  }

  /** Resumes counting without touching what was already banked. */
  #releaseSegment() {
    if (this.#runningSince) return;

    this.#runningSince = performance.now();
  }

  #cancelBridge() {
    if (!this.#bridge) return;

    clearTimeout(this.#bridge);
    this.#bridge = undefined;
  }

  /**
   * @param {number} index
   */
  #setIndex(index) {
    this.#index = index;

    // Where this slide starts and ends within the whole cycle. The animation
    // offset uses the start; the reduced-motion read-out uses the end, so the
    // static bar reaches 100% on the last slide.
    this.#syncTiming();

    const position = (index + 1) / this.#slideCount;
    this.style.setProperty(POSITION_PROPERTY, `${Math.min(1, Math.max(0, position))}`);

    // The dots stay in the DOM as the accessible, keyboard-reachable slide
    // navigation. Slideshow#current already keeps aria-selected in step via
    // ref="dots[]"; aria-current is the attribute assistive tech actually
    // announces on a button, so add it alongside.
    this.#dots.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /** @param {Event & { detail?: { index?: number, trigger?: string, userInitiated?: boolean } }} event */
  #handleSelect = (event) => {
    const detail = event.detail;
    const index = detail?.index;

    if (typeof index !== 'number' || Number.isNaN(index)) return;

    // A swipe leaves autoplay switched off in the stock component: the drag
    // handler calls pause(), which sets [paused], and the resume() that follows
    // on pointerup returns early precisely because paused is true - so the timer
    // is never re-armed and the bar would sit frozen for good after one swipe.
    // Clearing the flag here, while that resume() is still waiting behind the
    // settle scroll, lets the component start its own interval again.
    if (detail?.trigger === 'drag') {
      this.#slideshow?.removeAttribute('paused');
    } else if (index === this.#index) {
      // select() and the scroll handler can both report the same slide; only a
      // real change means a new interval.
      return;
    } else if (detail?.userInitiated) {
      // A dot, an arrow or a manual track scroll. Slideshow#select() leaves the
      // running interval alone, so without this the fill would be re-based
      // against a timer that is already part-way through its cycle. Skipped for
      // 'drag', whose own resume() runs after the settle scroll, and for the
      // autoplay tick, which reports userInitiated: false and is already in
      // phase.
      this.#rearmTimer();
    }

    // The slide is changing, so any bridge timer still counting down the last
    // slide's remainder is now meaningless - including the one that fired to
    // get here, whose id is simply stale.
    this.#cancelBridge();

    // Re-base the cycle on the new slide - delay becomes -(index * interval) -
    // and replay. On an autoplay tick the fill is already exactly there, so the
    // jump is invisible and the bar simply keeps travelling; on a manual jump it
    // moves to that slide's starting proportion and continues from there.
    this.#setIndex(index);
    this.#restart();
    this.#beginSegment();
  };

  /**
   * Asks the slideshow to start a fresh full interval, using nothing but its
   * public resume(). resume() already no-ops when autoplay is off or the
   * slideshow is paused; the guards here repeat that so this stays correct if
   * the element has not upgraded yet, and so an explicit pause is never undone.
   *
   * resume() is pause() + play(), i.e. [paused] is set and cleared within one
   * task. The MutationObserver below sees the pair as one microtask and fires a
   * second #restart(); no frame is painted between the two, so the fill simply
   * starts once.
   */
  #rearmTimer() {
    const slideshow = this.#slideshow;

    if (!slideshow || !this.#interval) return;
    if (slideshow.hasAttribute('paused')) return;

    const { resume } = this.#playback;

    if (typeof resume === 'function') resume.call(slideshow);
  }

  // Pointer in: the component has already cleared its timer via suspend(), and
  // the stylesheet has already held the fill on :hover. Hold the stopwatch with
  // them so the three stay in agreement.
  #handleMouseEnter = () => {
    this.#cancelBridge();
    this.#holdSegment();
  };

  // Pointer out: the component's own mouseleave listener runs resume() and arms
  // a brand new FULL interval. Ours has to run after it to undo that, and
  // queueMicrotask lands after every listener for this event regardless of which
  // order the two registered in.
  #handleMouseLeave = () => {
    // Set SYNCHRONOUSLY, inside the event dispatch, so it is already true no
    // matter which of the two microtasks below runs first: this one, or the
    // MutationObserver callback the component's resume() just queued.
    this.#leavingHover = true;
    queueMicrotask(() => this.#bridgeRemainder());
    // Cleared on a macrotask, which runs only after the entire microtask
    // checkpoint has drained - so the flag cannot expire midway through it.
    setTimeout(() => {
      this.#leavingHover = false;
    }, 0);
  };

  /**
   * Replaces the component's fresh full interval with a one-shot timer for the
   * time the current slide actually had left, so leaving a hover continues the
   * dwell instead of starting it over. The fill is left alone: it un-pauses on
   * its own the moment :hover stops matching and carries on from where it was
   * frozen, which is precisely what this timer is measuring to.
   */
  #bridgeRemainder() {
    const slideshow = this.#slideshow;
    const intervalMs = this.#interval * 1000;

    if (!slideshow || !slideshow.isConnected || !intervalMs) return;
    // An explicit pause outranks a hover, and the visibility handler owns the
    // hidden-tab case - neither should be overridden from here.
    if (slideshow.hasAttribute('paused') || document.hidden) return;
    // The pointer is back already; #handleMouseEnter will hold things again.
    if (slideshow.matches(':hover')) return;

    const remaining = Math.max(0, intervalMs - this.#elapsed);
    const { suspend, next, play } = this.#playback;

    if (typeof suspend !== 'function' || typeof next !== 'function' || typeof play !== 'function') return;

    // suspend(), not pause(): it clears the timer without setting [paused], so
    // the stylesheet keeps the fill running.
    suspend.call(slideshow);
    this.#releaseSegment();

    this.#bridge = setTimeout(() => {
      this.#bridge = undefined;

      if (!slideshow.isConnected || slideshow.hasAttribute('paused')) return;

      // Advance, then hand the timing back to the component's own interval for
      // every slide after this one. next() dispatches slideshow:select, so
      // #handleSelect re-bases the bar and restarts the stopwatch.
      next.call(slideshow);
      play.call(slideshow);
    }, remaining);
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.setAttribute('tab-hidden', '');
      this.#cancelBridge();
      this.#holdSegment();
      return;
    }

    if (!this.hasAttribute('tab-hidden')) return;

    this.removeAttribute('tab-hidden');
    this.#restart();
    this.#beginSegment();
  };

  #handleMotionPreference = () => {
    this.#setIndex(this.#index);
    this.#restart();
  };

  /** @param {MutationRecord[]} records */
  #handleSlideshowAttributes = (records) => {
    for (const record of records) {
      if (record.attributeName === 'autoplay') {
        this.#syncTiming();
        this.#restart();
        continue;
      }

      if (record.attributeName !== 'paused') continue;

      // [paused] set means the timer is gone and the fill is held - by the
      // play/pause control, or by the drag handler mid-swipe.
      if (this.#slideshow?.hasAttribute('paused')) {
        this.#cancelBridge();
        this.#holdSegment();
        continue;
      }

      // [paused] cleared means play() has just armed a brand new interval.
      if (record.oldValue !== null) {
        /*
         * ...EXCEPT when it is the tail of a hover exit, which is the case
         * this whole element exists to get right.
         *
         * Slideshow binds mouseleave -> resume(), and resume() is pause() +
         * play(): [paused] goes on and straight back off inside one task. That
         * clear is indistinguishable here from someone pressing the play
         * button, so this branch used to fire on every hover exit and call
         * #beginSegment(), zeroing the stopwatch a microtask BEFORE
         * #bridgeRemainder() read it. The bridge then measured a full interval
         * of remaining time and the slide restarted its dwell - the exact
         * symptom the bridge was written to remove. Measured: 6031ms from
         * mouse-leave to the next slide on a 6s deck, i.e. no resume at all.
         *
         * When the flag is set, the bridge owns this transition: it re-arms
         * the timer itself and deliberately leaves the fill running so it
         * carries on from where the pointer froze it.
         */
        if (this.#leavingHover) continue;

        this.#restart();
        this.#beginSegment();
      }
    }
  };
}

if (!customElements.get('detailer-slideshow-progress')) {
  customElements.define('detailer-slideshow-progress', DetailerSlideshowProgress);
}
